import {
    ContentCopyOutlined,
    DeleteOutlined,
    LayersClearOutlined,
    LibraryAddOutlined,
    LockOpenOutlined,
    LockOutlined,
} from '@mui/icons-material';
import { Divider, ListItemText, Menu, MenuItem } from '@mui/material';
import { Replace } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import MenuAction from '~/components/Menu/MenuAction';
import MenuFlyout from '~/components/Menu/MenuFlyout';
import MenuFlyoutRow from '~/components/Menu/MenuFlyoutRow';
import StructureColorMenuItem from '~/components/Structures/StructureColorMenuItem';
import { RoiReadModel } from '~/domain/readmodels/StructureSetReadModel';
import { useClearContourExtent } from '~/hooks/structure-set/useClearContourExtent';
import { useCopyStructureSetFlow } from '~/hooks/structure-set/useCopyStructureSetFlow';
import useRoi from '~/hooks/structure-set/useRoi';
import {
    structureRowAnchor,
    useColorEditWithheldReasonId,
    useStructureColorEdit,
} from '~/hooks/structure-set/useStructureColorEdit';
import { useShownSlicePlaneOrRefusal } from '~/hooks/structure-set/useShownSlicePlaneOrRefusal';
import { openedStructureDeletionConfirmationDialogState } from '~/state/atoms/structureSet/roiAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import CopyStructureSetDialog from '../../../../CopyStructureSetDialog/CopyStructureSetDialog';

/** Wording shared with the viewport menu: one operation, one report. */
const CLEAR_MESSAGES = {
    successMessageId: 'contourDeletion.extent.success',
    failedMessageId: 'contourDeletion.extent.failed',
    undoLabelId: 'contourDeletion.undo',
    undoUnavailableMessageId: 'contourDeletion.undoUnavailable',
    logLabel: 'Delete contours',
} as const;

/**
 * The More menu of a structure: change its colour; duplicate it, copy its
 * contours over another structure or copy it to another image; clear its
 * contours on the shown slice or on all slices; lock or unlock it; delete
 * it after a confirmation. The colour item says why when it cannot run, and
 * Clear Contours on Slice says why when no acquired slice is shown; the
 * other items are disabled without a reason. The menu hangs from
 * `anchorPosition` by its bottom-left corner.
 */
export default function StructureMoreMenu({
    roi,
    anchorPosition,
    colorAnchorEl,
    openerEl,
    onClose,
}: {
    roi: RoiReadModel;
    /** Where the menu opens; it is closed while undefined. */
    anchorPosition: { left: number; top: number } | undefined;
    /** Beside which the colour edit opens when the structure's row is off screen. */
    colorAnchorEl: HTMLElement | null;
    /** The button that opened the menu; focus returns to it from the colour edit. */
    openerEl: HTMLElement | null;
    onClose: () => void;
}) {
    const intl = useIntl();
    const { isRoiEmpty, copyRoi, copySdfOver, setLocked } = useRoi(roi);
    const structureSet = useRecoilValue(currentStructureSetState);
    const setOpenedDeletionDialog = useSetRecoilState(
        openedStructureDeletionConfirmationDialogState
    );
    const { open: openColorEdit } = useStructureColorEdit();
    const colorWithheldReasonId = useColorEditWithheldReasonId(roi);
    const [isCopyDialogOpen, setIsCopyDialogOpen] = useState(false);
    const [replaceRowEl, setReplaceRowEl] = useState<HTMLLIElement | null>(
        null
    );
    const [isReplaceOpen, setIsReplaceOpen] = useState(false);
    const closeCopyDialog = useCallback(() => setIsCopyDialogOpen(false), []);
    // Single shared copy flow, scoped to this one ROI in the dialog below.
    const { patient, domainStructureSet, sourceImage, handleConfirmCopy } =
        useCopyStructureSetFlow(structureSet ?? null, closeCopyDialog);
    const scopedRoiNumbers = useMemo(
        () => new Set([roi.roiNumber]),
        [roi.roiNumber]
    );

    const hasContourData = !isRoiEmpty();
    const structureSetUid = structureSet?.dicomUid ?? '';
    const canCopy = Boolean(
        patient && domainStructureSet && sourceImage && hasContourData
    );
    const otherRois = structureSet?.roiArray.filter(
        (r) => r.roiNumber !== roi.roiNumber
    );

    // The slice a tile of the edit target shows, so the slice item acts on
    // the structure set the user is editing and the slice its tiles show.
    // None on a tilted series, with every tile turned off the slices, or
    // with no tile of the target on the grid. Asked here rather than left to
    // the write: with no resolvable slice the write clears nothing and
    // reports "no contours to delete", which is false about a structure full
    // of contour the user can see. The item is refused with the reason.
    const {
        plane,
        readPlane,
        refusalMessageId: sliceRefusalId,
    } = useShownSlicePlaneOrRefusal('clearStructure');
    // Addressed to THIS structure, not the selection: the report and the
    // Undo it carries must name the structure the menu was opened on.
    const { clearContourExtent } = useClearContourExtent(
        structureSet?.dicomUid,
        CLEAR_MESSAGES,
        roi.roiNumber
    );

    // Each action closes the menu first, so a dialog or report it opens is
    // not covered by it.
    const act = (action: () => void) => () => {
        setIsReplaceOpen(false);
        onClose();
        action();
    };
    const closeMenu = () => {
        setIsReplaceOpen(false);
        onClose();
    };

    const handleChangeColor = () => {
        onClose();
        const anchor = structureRowAnchor(roi.roiNumber, colorAnchorEl);
        if (anchor) {
            openColorEdit(roi.roiNumber, 'contextMenu', anchor, openerEl);
        }
    };

    return (
        <>
            <Menu
                open={anchorPosition !== undefined}
                onClose={closeMenu}
                anchorReference="anchorPosition"
                anchorPosition={anchorPosition}
                transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                marginThreshold={8}
                data-testid="structure-more-menu"
                slotProps={{ paper: { sx: { width: 260, mt: 0 } } }}
            >
                <StructureColorMenuItem
                    roi={roi}
                    onClick={handleChangeColor}
                    disabled={colorWithheldReasonId !== undefined}
                    withheldReasonId={colorWithheldReasonId}
                />
                <Divider />
                {!roi.isPointRoi && (
                    <MenuAction
                        icon={<LibraryAddOutlined />}
                        label={<FormattedMessage id="structure.duplicate" />}
                        onClick={act(() => copyRoi(structureSetUid))}
                        disabled={!hasContourData || !structureSetUid}
                    />
                )}
                {!roi.isPointRoi && otherRois && otherRois.length > 0 && (
                    <MenuFlyoutRow
                        ref={setReplaceRowEl}
                        open={isReplaceOpen}
                        onToggle={() => setIsReplaceOpen((open) => !open)}
                        onOpen={() => setIsReplaceOpen(true)}
                        Icon={Replace}
                        name={
                            <FormattedMessage id="structure.replaceAnotherStructure" />
                        }
                        data-testid="replace-structure-row"
                    />
                )}
                <MenuAction
                    icon={<ContentCopyOutlined />}
                    label={
                        <FormattedMessage
                            id="structureSet.copy"
                            defaultMessage="Copy to..."
                        />
                    }
                    onClick={act(() => setIsCopyDialogOpen(true))}
                    disabled={!canCopy}
                    data-testid="copy-structure-to-menu-item"
                />
                {/* Emptying a structure and DELETING it are different acts,
                    so they sit apart: these two clear contours and leave the
                    structure standing, the one at the end removes it. */}
                {!roi.isPointRoi && <Divider />}
                {!roi.isPointRoi && (
                    <MenuAction
                        icon={<LayersClearOutlined />}
                        label={
                            <FormattedMessage id="contourDeletion.clearSlice" />
                        }
                        // Read at the click, never captured at render: a
                        // scroll between the two must not clear the slice
                        // shown earlier.
                        onClick={act(
                            () =>
                                void clearContourExtent({
                                    scope: 'this-slice',
                                    plane: readPlane(),
                                    addressedBy: 'acquisition',
                                    offerUndo: true,
                                })
                        )}
                        disabled={!hasContourData || !roi.canModifyContours}
                        disabledReason={
                            hasContourData &&
                            roi.canModifyContours &&
                            plane === null
                                ? intl.formatMessage({ id: sliceRefusalId })
                                : undefined
                        }
                        data-testid="clear-slice-menu-item"
                    />
                )}
                {!roi.isPointRoi && (
                    <MenuAction
                        icon={<LayersClearOutlined />}
                        label={<FormattedMessage id="structure.clearAll" />}
                        onClick={act(
                            () =>
                                void clearContourExtent({
                                    scope: 'all-slices',
                                    offerUndo: true,
                                })
                        )}
                        disabled={!hasContourData || !roi.canModifyContours}
                        data-testid="clear-all-contours-menu-item"
                    />
                )}
                <Divider />
                <MenuAction
                    icon={roi.locked ? <LockOpenOutlined /> : <LockOutlined />}
                    label={
                        <FormattedMessage
                            id={
                                roi.locked
                                    ? 'structure.unlock'
                                    : 'structure.lock'
                            }
                        />
                    }
                    note={<FormattedMessage id="structure.lockDicomBoundary" />}
                    onClick={act(() => setLocked(!roi.locked))}
                    data-testid="lock-structure-menu-item"
                />
                <Divider />
                <MenuAction
                    icon={<DeleteOutlined />}
                    destructive
                    label={<FormattedMessage id="structure.delete" />}
                    onClick={act(() => setOpenedDeletionDialog(roi.roiNumber))}
                    disabled={!roi.canModifyContours}
                    data-testid="delete-structure-button"
                />
            </Menu>
            {/* A sibling of the menu, not a child: a child would hand its
                arrow keys to the menu too, which then moves focus twice. */}
            <MenuFlyout
                anchorEl={replaceRowEl}
                open={anchorPosition !== undefined && isReplaceOpen}
                onClose={() => setIsReplaceOpen(false)}
                data-testid="replace-structure-menu"
            >
                {otherRois?.map((target) => (
                    <MenuItem
                        key={target.roiNumber}
                        onClick={act(() =>
                            copySdfOver(structureSetUid, target.roiNumber)
                        )}
                        disabled={
                            !hasContourData ||
                            !structureSetUid ||
                            !target.canModifyContours
                        }
                    >
                        <ListItemText>{target.name}</ListItemText>
                    </MenuItem>
                ))}
            </MenuFlyout>
            {isCopyDialogOpen && structureSet && patient && sourceImage && (
                <CopyStructureSetDialog
                    open={isCopyDialogOpen}
                    onClose={closeCopyDialog}
                    onConfirm={handleConfirmCopy}
                    sourceStructureSet={structureSet}
                    patient={patient}
                    sourceImage={sourceImage}
                    scopedRoiNumbers={scopedRoiNumbers}
                />
            )}
        </>
    );
}
